export interface ButtonConfig {
  readonly x: number
  readonly y: number
}

export type IconicRange = 'Iconic' | 'Styl' | 'Essence' | 'Pro' | 'Outdoor'
export type IconicColor = 'SV' | 'SH' | 'VW' | 'XW' | 'WY' | 'CY' | 'AN' | 'CE' | 'AW' | 'AG' | 'XB' | 'ProXW' | 'ProBK' | 'ProDB' | 'ProGR' | 'ProRD' | 'OutdoorXW' | 'OutdoorBK'

export type IconicColorImportName =
  'Styl Silver' | 'Styl Silver Shadow' | 'Styl Crowne'
  | 'Iconic Vivid White' | 'Iconic Vivid White Solid Edge' | 'Iconic Warm Grey' | 'Iconic Cool Grey' | 'Iconic Anthracite' | 'Iconic Black'
  | 'Essence Arctic White' | 'Essence Ash Grey'
  | 'Pro White' | 'Pro Black' | 'Pro Blue' | 'Pro Red' | 'Pro Green'
  | 'Outdoor White' | 'Outdoor Black'

export type IconicColorDisplayName =
  'Silver' | 'Silver Shadow' | 'Vivid White' | 'Xtra White' | 'Warm Grey' | 'Cool Grey' | 'Anthracite' | 'Crowne'
  | 'Arctic White' | 'Ash Grey' | 'Xtra Black' | 'White' | 'Black' | 'Blue' | 'Red' | 'Green'
export type BaseProductType = 'Switch' | 'Socket' | 'Other'

export const TopLevelMenuCategories = ['Switch', 'Socket', 'Other', 'Outdoor', 'Wiser'] as const

export type TopLevelMenuCategory = typeof TopLevelMenuCategories[number]

export type ProductOrientation = 'Horizontal' | 'Vertical'
export type ProductOrientationSupport = ProductOrientation | 'Both'

/**
 * A Skin, Grid (optional), and Orientation configuration or products that can be selected and added to the list
 */
export interface SkinProductOption extends SkinProduct {
  readonly gridProduct: GridProduct | null
  readonly orientation: ProductOrientation
}

export function asSkinProductOptionWithGridOrNull(it: SkinProductOption): SkinProductOptionWithGrid | null {
  if (it.gridProduct !== null) {
    return it as SkinProductOptionWithGrid
  }

  return null
}

/**
 * Specialisation of {@link SkinProductOption} which has a Grid
 */
export interface SkinProductOptionWithGrid extends SkinProductOption {
  gridProduct: GridProduct
}

export type InterfaceProductCategory = 'Switches' | 'Dimmers' | 'Rotary Switches' | 'USB' | 'Bluetooth' | 'LED' | 'Network' | 'Accessories' | 'Pro Placeholder'

export enum InterfaceProductSubcategory {
  Wiser = 'Wiser Push Button Switches',
  RemoteSwitches = 'Remote Push Button Switches',
  CBus = 'C-Bus Switches',
  Pictograms = 'Pictogram Rockers',
}

export interface InterfaceProduct {
  readonly itemNo: string
  readonly description: string
  readonly name: string
  readonly image: string
  readonly category: InterfaceProductCategory
  readonly subcategory: string | null
  readonly style: IconicRange | null
  readonly color: IconicColor | null
  readonly baseItemNo: string | null
  readonly mechs: readonly string[]
  readonly displayedMechs: readonly string[] | null
  readonly packQuantity: number
  // v2
  readonly includedComponents?: readonly string[] | null
  // v2.6
  readonly disableLedSupport?: boolean
  // vPro
  readonly pro?: boolean
  readonly proCompatible?: boolean
}

export type SkinProduct = {
  readonly itemNo: string
  readonly description: string
  readonly displayName: string
  readonly image: string
  readonly imageScale: number | null
  readonly imageOrientation: ProductOrientation
  readonly grid: string | null
  readonly style: IconicRange | null
  readonly color: IconicColor | null
  readonly includedRockerItemNo: string | null
  readonly type: BaseProductType
  // v2
  readonly disableSlots?: boolean
  readonly orientationSupport?: ProductOrientationSupport
  // v2.5
  readonly otherOrientation?: string | null
  // v3
  readonly pro?: boolean
  // v6
  readonly displaySubtitle?: string | null
  readonly menuCategories?: TopLevelMenuCategory[]
  readonly displaySubcategory?: string | null
}

export interface MechProduct {
  readonly itemNo: string
  readonly description: string
  readonly displayName?: string
  readonly image: string
  readonly supportsLed: boolean
  readonly includedInterfaceItemNo: string | null
  readonly interfaceDisplayNameOverride: string | null
  readonly category: InterfaceProductCategory
  readonly subcategory: string | null
  // v2
  readonly includesLed?: boolean
  readonly pro?: boolean
  readonly proCompatible?: boolean
}

export interface Coordinate { x: number, y: number }

export interface GridProduct {
  readonly itemNo: string
  readonly description: string
  readonly displayName: string
  readonly comparisonName: string | null
  readonly type: BaseProductType
  readonly socketNo: number | null
  readonly gangNo: number | null
  readonly slotCount: number
  readonly orientation: ProductOrientationSupport
  /** @Deprecated use SkinProduct.otherOrientation */
  readonly otherOrientation: string | null
  readonly slotPositions: readonly Coordinate[]
  readonly baseItemNo: string | null
  readonly includedSkinItemNo: string | null
  readonly includedMechItemNo: string | null
  readonly standaloneGridItemNo: string | null
  readonly pro?: boolean
}

export const ProductRuleTypeRequiresOtherMech = 'RequiresOtherMech' as const
export const ProductRuleTypeRequiresWiserHub = 'RequiresWiserHub' as const
export const ProductRuleTypeRequiresMomentarySwitches = 'RequiresMomentarySwitches' as const
export const ProductRuleTypeMaxGangNo = 'MaxGangNo' as const
export const ProductRuleTypeSwitchOnly = 'SwitchOnly' as const
export const ProductRuleTypeIncompatibleGrids = 'IncompatibleGrids' as const
export const ProductRuleTypeExclusiveCategory = 'ExclusiveCategory' as const

interface BaseProductRule {
  ruleType:
    typeof ProductRuleTypeRequiresOtherMech
  | typeof ProductRuleTypeRequiresWiserHub
  | typeof ProductRuleTypeRequiresMomentarySwitches
  | typeof ProductRuleTypeMaxGangNo
  | typeof ProductRuleTypeSwitchOnly
  | typeof ProductRuleTypeIncompatibleGrids
  | typeof ProductRuleTypeExclusiveCategory
  products?: readonly string[]
}

export interface ProductRuleMaxGangNo extends BaseProductRule {
  products: readonly string[]
  ruleType: typeof ProductRuleTypeMaxGangNo
  maxGangNumber: number
  orientation: ProductOrientationSupport
}

export interface ProductRuleRequiresOtherMech extends BaseProductRule {
  products: readonly string[]
  ruleType: typeof ProductRuleTypeRequiresOtherMech
  otherMechs: readonly string[]
}

export interface ProductRuleRequiresWiserHub extends BaseProductRule {
  products: readonly string[]
  ruleType: typeof ProductRuleTypeRequiresWiserHub
}

export interface ProductRuleRequiresMomentarySwitches extends BaseProductRule {
  products: readonly string[]
  ruleType: typeof ProductRuleTypeRequiresMomentarySwitches
  overrideMech: string | null
}

export interface ProductRuleIncompatibleGrids extends BaseProductRule {
  products: readonly string[]
  ruleType: typeof ProductRuleTypeIncompatibleGrids
  incompatibleGrids: readonly string[]
}

export interface ProductRuleSwitchOnly extends BaseProductRule {
  products: readonly string[]
  ruleType: typeof ProductRuleTypeSwitchOnly
}

export interface ProductRuleExclusiveCategory extends BaseProductRule {
  category: string
  subcategory: string | null
  ruleType: typeof ProductRuleTypeExclusiveCategory
  products?: undefined
}

export type ProductRule = ProductRuleMaxGangNo | ProductRuleRequiresOtherMech | ProductRuleRequiresWiserHub | ProductRuleRequiresMomentarySwitches | ProductRuleSwitchOnly | ProductRuleIncompatibleGrids | ProductRuleExclusiveCategory

export enum ProductSeries {
  Iconic = 'Iconic',
  Pro = 'Pro',
}

export interface ConstantProductsShape {
  readonly WiserHub: string
  readonly WiserSwitchDefaultMech: string
}